import type {
  ApplicationWithStats,
  ConversationDetail,
  ConversationSummary,
  ConversationVerdict,
} from '@owl/shared';
import { Fragment, useEffect, useState } from 'react';
import { useConfirm } from '../components/Confirm';
import type { ShellCtx } from '../components/Shell';
import { formatInt, formatUsd } from '../components/charts/AiUsageBlocks';
import { activateOnKey } from '../lib/activateOnKey';
import { ApiError, api } from '../lib/api';
import { formatDateTime } from '../lib/time';

const VERDICT_COLOR: Record<ConversationVerdict, string> = {
  pass: '#2e9e5b',
  partial: '#c9921f',
  fail: '#d9534f',
  na: '#888',
};

function VerdictBadge({ verdict }: { verdict: ConversationVerdict | null }) {
  if (!verdict) return <span className="dim">—</span>;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        color: '#fff',
        background: VERDICT_COLOR[verdict],
      }}
    >
      {verdict}
    </span>
  );
}

type TimeWindow = '24h' | '7d' | '30d' | 'all';

const WINDOW_MS: Record<Exclude<TimeWindow, 'all'>, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

function fromForWindow(w: TimeWindow): string | undefined {
  if (w === 'all') return undefined;
  return new Date(Date.now() - WINDOW_MS[w]).toISOString();
}

// Render one transcript turn. customer on the left, agent on the right, anything
// else (context, tool_call, tool_result, …) as a dim full-width block.
function Turn({ role, text, name }: { role: string; text: string; name?: string }) {
  const isCustomer = role === 'customer';
  const isAgent = role === 'agent' || role === 'VERA';
  const isMessage = isCustomer || isAgent;

  if (!isMessage) {
    return (
      <div
        style={{
          alignSelf: 'stretch',
          background: 'var(--bg-1)',
          border: '1px dashed var(--border-1)',
          borderRadius: 8,
          padding: '6px 10px',
          fontSize: 12,
          whiteSpace: 'pre-wrap',
        }}
      >
        <div
          className="dim mono"
          style={{ fontSize: 10, marginBottom: 2, textTransform: 'uppercase' }}
        >
          {role}
          {name ? ` · ${name}` : ''}
        </div>
        {text}
      </div>
    );
  }

  return (
    <div
      style={{
        alignSelf: isCustomer ? 'flex-start' : 'flex-end',
        maxWidth: '85%',
        background: isCustomer ? 'var(--bg-1)' : 'var(--accent-2, #2b5fb3)',
        color: isCustomer ? 'inherit' : '#fff',
        border: isCustomer ? '1px solid var(--border-1)' : 'none',
        borderRadius: 10,
        padding: '6px 10px',
        fontSize: 12.5,
        whiteSpace: 'pre-wrap',
      }}
    >
      <div
        className="dim"
        style={{
          fontSize: 10,
          marginBottom: 2,
          color: isCustomer ? 'var(--fg-2)' : 'rgba(255,255,255,0.8)',
        }}
      >
        {isCustomer ? 'Customer' : 'VERA'}
      </div>
      {text}
    </div>
  );
}

function ConversationRow({ conv }: { conv: ConversationSummary }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [judging, setJudging] = useState(false);

  // The detail (once loaded / re-judged) is the source of truth for the verdict;
  // fall back to the list summary before it is fetched.
  const verdict = detail?.verdict ?? conv.verdict;
  const score = detail?.score ?? conv.score;

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && !detail) {
      api
        .getConversation(conv.id)
        .then(setDetail)
        .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load'));
    }
  }

  function judge() {
    setJudging(true);
    setError(null);
    api
      .judgeConversation(conv.id)
      .then(setDetail)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Judge failed'))
      .finally(() => setJudging(false));
  }

  return (
    <Fragment>
      <tr
        className="data-row"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => activateOnKey(e, toggle)}
      >
        <td>
          <div>
            <strong>{conv.applicationName ?? 'unknown app'}</strong>
            {conv.channel && (
              <span className="dim" style={{ marginLeft: 6, fontSize: 11 }}>
                {conv.channel}
              </span>
            )}
          </div>
          <div className="dim" style={{ fontSize: 11 }}>
            {formatDateTime(conv.createdAt)}
          </div>
        </td>
        <td>
          <VerdictBadge verdict={verdict} />
          {score !== null && (
            <span className="mono" style={{ marginLeft: 6, fontSize: 12 }}>
              {score}
            </span>
          )}
        </td>
        <td>
          <span className="mono" style={{ fontSize: 12 }}>
            {conv.outcome ?? '—'}
          </span>
        </td>
        <td className="mono">{conv.turns}</td>
        <td className="mono" style={{ fontSize: 12 }}>
          {formatInt(conv.inputTokens)} / {formatInt(conv.outputTokens)}
        </td>
        <td className="mono">{formatUsd(conv.productCostUsd)}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} style={{ background: 'var(--bg-2)' }}>
            {error && <div className="error-banner">{error}</div>}
            {!detail && !error ? (
              <div className="dim" style={{ fontSize: 12 }}>
                Loading…
              </div>
            ) : detail ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <button
                    type="button"
                    className="btn btn-accent"
                    onClick={judge}
                    disabled={judging}
                  >
                    {judging ? 'Auditando…' : detail.verdict ? 'Re-auditar' : 'Auditar ahora'}
                  </button>
                  {detail.judgeModel && (
                    <span className="dim mono" style={{ fontSize: 11 }}>
                      {detail.judgeModel}
                      {detail.judgeCostUsd !== null ? ` · ${formatUsd(detail.judgeCostUsd)}` : ''}
                    </span>
                  )}
                </div>
                {detail.verdict && (
                  <div style={{ marginBottom: 10 }}>
                    {detail.reasoning && (
                      <div style={{ marginBottom: 6 }}>
                        <strong style={{ fontSize: 12 }}>Judge:</strong>{' '}
                        <span style={{ fontSize: 12.5 }}>{detail.reasoning}</span>
                      </div>
                    )}
                    {detail.issues.length > 0 && (
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5 }}>
                        {detail.issues.map((iss) => (
                          <li key={iss}>{iss}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 760 }}>
                  {detail.transcript.length === 0 ? (
                    <div className="dim" style={{ fontSize: 12 }}>
                      (empty transcript)
                    </div>
                  ) : (
                    detail.transcript.map((turn, i) => (
                      <Turn
                        // Transcript turns have no stable id; index is fine for a static list.
                        key={`${i}:${turn.role}`}
                        role={turn.role}
                        text={turn.text}
                        name={turn.name}
                      />
                    ))
                  )}
                </div>
              </>
            ) : null}
          </td>
        </tr>
      )}
    </Fragment>
  );
}

export function Conversations({ ctx }: { ctx: ShellCtx }) {
  const [items, setItems] = useState<ConversationSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [apps, setApps] = useState<ApplicationWithStats[]>([]);
  const [appId, setAppId] = useState<string>('');
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('7d');
  const [auditing, setAuditing] = useState(false);
  const confirm = useConfirm();

  useEffect(() => {
    api
      .listApplications()
      .then((res) => setApps(res.items))
      .catch(() => setApps([]));
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch on refreshTick
  useEffect(() => {
    let active = true;
    setItems(null);
    api
      .listConversations({
        applicationId: appId || undefined,
        from: fromForWindow(timeWindow),
        limit: 200,
      })
      .then((res) => {
        if (active) setItems(res.items);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : 'Failed to load');
      });
    return () => {
      active = false;
    };
  }, [appId, timeWindow, ctx.refreshTick]);

  async function triggerAudit() {
    if (!items || items.length === 0) return;
    const n = Math.min(items.length, 100);
    const ok = await confirm({
      title: '¿Lanzar auditoría?',
      sub: `${n} conversaciones`,
      body: 'El juez evalúa cada conversación con un modelo de IA. Consume tokens.',
      confirmLabel: 'Auditar',
    });
    if (!ok) return;
    setAuditing(true);
    setError(null);
    api
      .triggerAudit({
        applicationId: appId || undefined,
        from: fromForWindow(timeWindow),
        limit: n,
      })
      .then((res) => ctx.navigate({ name: 'auditdetail', id: res.id }))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Audit failed'))
      .finally(() => setAuditing(false));
  }

  return (
    <div className="view-pad">
      <div className="view-head">
        <div>
          <div className="view-title">Conversations</div>
          <div className="view-sub">
            {items?.length ?? 0} real captured conversations · full context &amp; agent round-trip
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="form-select" value={appId} onChange={(e) => setAppId(e.target.value)}>
            <option value="">All applications</option>
            {apps.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <select
            className="form-select"
            value={timeWindow}
            onChange={(e) => setTimeWindow(e.target.value as TimeWindow)}
          >
            <option value="24h">Last 24h</option>
            <option value="7d">Last 7d</option>
            <option value="30d">Last 30d</option>
            <option value="all">All time</option>
          </select>
          <button
            type="button"
            className="btn btn-accent"
            onClick={() => void triggerAudit()}
            disabled={auditing || !items || items.length === 0}
          >
            {auditing ? 'Lanzando…' : 'Auditar lote'}
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {items === null ? (
        <div className="empty-state">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          No conversations captured yet. Applications publish them to{' '}
          <code>POST /v1/conversations</code>.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Application</th>
                <th>Verdict</th>
                <th>Outcome</th>
                <th>Turns</th>
                <th>Tokens (in/out)</th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              {items.map((conv) => (
                <ConversationRow key={conv.id} conv={conv} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
